import { type InitialQueryBuilder, inArray } from "@tanstack/db";
import equal from "fast-deep-equal";

import type { TmsEvent } from "@storyboard/web-client";
import { serverNow } from "@storyboard/web-client";
import { intervalsCovering } from "@/models/repositories/AssignmentInterval";
import { attributedMembershipIdsOf } from "@/models/tmsEvent/attribution";
import { store } from "@/store/store";
import type { TmsEventRow } from "@/store/types";

/**
 * The store row for one wire event: the DTO plus the memberships it is attributed to, read from the assignment records
 * the store holds as of `now`, else the driver the event's facts name.
 */
export function tmsEventRowOf(event: TmsEvent, now: string): TmsEventRow {
  return {
    ...event,
    attributedMembershipIds: attributedMembershipIdsOf(event, (vehicleKey, integrationId, at) =>
      intervalsCovering(vehicleKey, integrationId, at, now),
    ),
  };
}

/**
 * Upserts TMS events into the store. Events are immutable facts keyed on the anchor id, so a repeat is
 * insert-or-keep; the only column that can change is the derived attribution, which the merge recomputes.
 */
export async function bulkUpsertTmsEvent(records: readonly TmsEvent[]): Promise<void> {
  if (records.length === 0) return;
  const now = serverNow().toISOString();
  store.upsert(
    "tmsEvent",
    records.map((event) => tmsEventRowOf(event, now)),
    keepAttributionIdentity,
  );
}

/**
 * Live query over the events attributed to one membership, for the feed and the Dispatch thread.
 *
 * TanStack cannot index an array field, so opening a feed scans the held events once and follows changes from there.
 * The scan is accepted: the collection holds `TMS_EVENT_BOOTSTRAP_DEPTH` of events, and non-live readers go through
 * `idx.tmsEventsByMembership`.
 */
export function tmsEventsForMembershipQuery(membershipId: string) {
  return (q: InitialQueryBuilder) =>
    q
      .from({ tmsEvent: store.collection("tmsEvent") })
      .where(({ tmsEvent }) => inArray(membershipId, tmsEvent.attributedMembershipIds));
}

/**
 * Keeps the stored attribution list when the recomputed one names the same drivers.
 *
 * The store compares rows field by field and a list by reference, so a re-fetched event would otherwise be a write.
 */
function keepAttributionIdentity(existing: TmsEventRow, incoming: TmsEventRow): TmsEventRow {
  if (!equal(existing.attributedMembershipIds, incoming.attributedMembershipIds)) return incoming;
  return { ...incoming, attributedMembershipIds: existing.attributedMembershipIds };
}
